/**
 * User search (typeahead) — backed by:
 *   GET /userservice/api/v3/user/typeahed?type=user&page=0&size=100&query=...
 *
 * Response shape (real example):
 *   {
 *     statusCode: "2XX",
 *     data: [
 *       { id, userTypeId, name, surName?, username,
 *         profileImage, profileImageThumbnail, isfollow,
 *         isBlocked, isPrivate }, …
 *     ]
 *   }
 */
import api from '@/api/client';
import { servicePath } from '@/config/env';
import { getInitials } from '@/lib/getInitials';
import { getUserTypeLabel } from '@/features/profile/userType';
import type { SearchUserResult } from '@/features/search/types';

interface ApiTypeaheadUser {
  readonly id?: number | string;
  readonly userTypeId?: number;
  readonly name?: string;
  readonly surName?: string;
  readonly username?: string;
  readonly profileImage?: string;
  readonly profileImageThumbnail?: string;
  readonly isVerified?: boolean;
  readonly userVerified?: boolean;
  readonly verified?: boolean;
  readonly isfollow?: number;
  readonly isBlocked?: boolean;
  readonly isPrivate?: boolean;
}

interface ApiTypeaheadResponse {
  readonly statusCode?: string;
  readonly data?: readonly ApiTypeaheadUser[];
}

export interface UserTypeaheadParams {
  readonly query: string;
  readonly page?: number;
  readonly size?: number;
  readonly type?: 'user';
  readonly signal?: AbortSignal;
}

function toSearchUserResult(item: ApiTypeaheadUser): SearchUserResult {
  const first = (item.name ?? '').trim();
  const last = (item.surName ?? '').trim();
  const displayName = `${first} ${last}`.trim() || item.username || 'User';
  const username = item.username ?? '';
  const avatarUrl = item.profileImageThumbnail || item.profileImage || undefined;
  const accountTypeLabel = getUserTypeLabel(item.userTypeId) ?? '';
  return {
    id: String(item.id ?? username),
    name: displayName,
    username,
    initials: getInitials(displayName),
    accountTypeLabel,
    verified: Boolean(item.isVerified ?? item.userVerified ?? item.verified ?? false),
    avatarUrl,
  };
}

/** Fetch user typeahead results. Returns `[]` for empty queries. */
export async function searchUsersTypeahead({
  query,
  page = 0,
  size = 100,
  type = 'user',
  signal,
}: UserTypeaheadParams): Promise<SearchUserResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const { data } = await api.get<ApiTypeaheadResponse>(servicePath('user', 'user/typeahed'), {
    params: { type, page, size, query: trimmed },
    signal,
  });

  return (data?.data ?? []).map(toSearchUserResult);
}
